import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner'
import AuthLayout from './AuthLayout'

const s = {
  heading: {
    fontSize: '1.6rem',
    fontWeight: 500,
    color: '#0f172a',
    marginBottom: 6,
    letterSpacing: '-0.02em',
  },
  sub: {
    fontSize: '0.9rem',
    color: '#64748b',
    marginBottom: 32,
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#475569',
    marginBottom: 6,
    letterSpacing: '0.02em',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d9e0',
    borderRadius: 8,
    fontSize: '0.95rem',
    color: '#0f172a',
    outline: 'none',
    background: '#f8fafc',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  },
  btn: {
    width: '100%',
    padding: '11px 0',
    background: '#84cc16',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 8,
    transition: 'background 0.2s',
  },
  btnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  error: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    color: '#dc2626',
    fontSize: '0.85rem',
    padding: '10px 14px',
    marginBottom: 20,
  },
  success: {
    background: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: 8,
    color: '#16a34a',
    fontSize: '0.85rem',
    padding: '10px 14px',
    marginBottom: 20,
  },
  divider: {
    borderTop: '1px solid #f1f5f9',
    margin: '24px 0',
  },
  link: {
    color: '#475569',
    fontSize: '0.85rem',
    textDecoration: 'underline',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
  },
}

function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const token = searchParams.get('token')
  const email = searchParams.get('email')

  const [isValid, setIsValid] = useState(false)
  const [loading, setLoading] = useState(true)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/validateResetToken', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, email }),
        })
        const result = await res.json()
        if (result.valid === true) setIsValid(true)
        else setErrorMessage(result.message || 'Invalid or expired reset link.')
      } catch {
        setErrorMessage('Server error while validating token.')
      } finally {
        setLoading(false)
      }
    }
    validate()
  }, [token, email])

  const handleReset = async e => {
    e.preventDefault()
    if (newPassword.length < 8) return setErrorMessage('Password must be at least 8 characters.')
    if (newPassword !== confirmPassword) return setErrorMessage('Passwords do not match.')

    setLoading(true)
    setErrorMessage('')
    try {
      const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/resetPassword', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, email, new_password: newPassword }),
      })
      const result = await res.json()
      if (result.success === true) {
        setSuccessMessage('Password updated. Redirecting to sign in...')
        setTimeout(() => navigate('/login'), 2500)
      } else {
        setErrorMessage(result.message || 'Failed to reset password.')
      }
    } catch {
      setErrorMessage('Server error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout
      quote="Set a strong new password and get back to scheduling."
      image="https://schedpilot.com/wp-content/uploads/2026/05/pexels-ofspace-16323586.jpg"
    >
      <p style={s.heading}>Set a new password</p>
      <p style={s.sub}>Must be at least 8 characters.</p>

      {loading && <LoadingSpinner />}
      {errorMessage && <div style={s.error}>{errorMessage}</div>}
      {successMessage && <div style={s.success}>{successMessage}</div>}

      {isValid && !loading && (
        <form onSubmit={handleReset}>
          <div style={{ marginBottom: 18 }}>
            <label style={s.label}>New password</label>
            <input
              style={s.input}
              type="password"
              value={newPassword}
              placeholder="••••••••"
              onChange={e => setNewPassword(e.target.value)}
              onFocus={e => e.target.style.borderColor = '#84cc16'}
              onBlur={e => e.target.style.borderColor = '#d1d9e0'}
              required
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={s.label}>Confirm password</label>
            <input
              style={s.input}
              type="password"
              value={confirmPassword}
              placeholder="••••••••"
              onChange={e => setConfirmPassword(e.target.value)}
              onFocus={e => e.target.style.borderColor = '#84cc16'}
              onBlur={e => e.target.style.borderColor = '#d1d9e0'}
              required
            />
          </div>

          <button
            type="submit"
            style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
            disabled={loading}
            onMouseOver={e => { if (!loading) e.target.style.background = '#65a30d' }}
            onMouseOut={e => { if (!loading) e.target.style.background = '#84cc16' }}
          >
            Update password
          </button>
        </form>
      )}

      <div style={s.divider} />

      <p style={{ fontSize: '0.85rem', color: '#7d8fa3', textAlign: 'center', margin: 0 }}>
        <button style={s.link} onClick={() => navigate('/login')}>
          Back to sign in
        </button>
      </p>
    </AuthLayout>
  )
}

export default ResetPassword
